// ============================================================================
// PARTICIPANT THREADS SERVICE
// Facilitator-facing read access to participant<->avatar 1:1 chat threads
// (Threaded Conversations, Participants nav), plus the on-demand Summarize +
// promote-to-graph flow (Phase B).
// ============================================================================

import { api } from './api';
import { ActivatedCognitiveElement, LinkedNodeRef } from './workshop';

export type ThreadCandidateType = 'story' | 'persona' | 'nfr' | 'adr' | 'insight';

export interface ThreadCandidate {
  id: string;
  type: ThreadCandidateType;
  content: Record<string, any>;
  confidence: number;
  status: 'pending' | 'confirmed' | 'dismissed';
}

export interface ThreadCandidateBatch {
  sessionId: string;
  avatarId: string;
  participantId: string;
  summary: string;
  candidates: ThreadCandidate[];
  createdAt: string;
}

export interface ParticipantThreadSummary {
  sessionId: string;
  avatarId: string;
  participantId: string;
  participantName: string;
  avatarName: string;
  createdAt: string;
  lastMessageAt: string;
  messageCount: number;
  archived?: boolean;
}

export interface ParticipantThreadMessage {
  sessionId: string;
  avatarId: string;
  participantId: string;
  messageId: string;
  timestamp: string;
  speaker: 'participant' | 'avatar';
  text: string;
  factsConsidered?: string[];
  assumptions?: string[];
  evidenceUsed?: string[];
  questionsRaised?: string[];
  confidence?: number;
  activatedElements?: ActivatedCognitiveElement[];
  reasoningDepth?: 'single' | 'multi-step';
  linkedNodes?: LinkedNodeRef[];
}

export async function listParticipantThreads(sessionId: string): Promise<ParticipantThreadSummary[]> {
  const { data } = await api.get(`/api/sessions/${sessionId}/participant-threads`);
  return data.threads;
}

export async function getParticipantThreadMessages(
  sessionId: string,
  avatarId: string,
  participantId: string
): Promise<ParticipantThreadMessage[]> {
  const { data } = await api.get(
    `/api/sessions/${sessionId}/participant-threads/${avatarId}/${participantId}/messages`
  );
  return data.messages;
}

export async function setThreadedConversationsEnabled(sessionId: string, enabled: boolean): Promise<void> {
  await api.patch(`/api/sessions/${sessionId}/threaded-conversations-enabled`, { enabled });
}

export async function summarizeThread(
  sessionId: string,
  avatarId: string,
  participantId: string
): Promise<{ requestId: string }> {
  const { data } = await api.post(
    `/api/sessions/${sessionId}/participant-threads/${avatarId}/${participantId}/summarize`
  );
  return data;
}

export async function getThreadCandidates(
  sessionId: string,
  avatarId: string,
  participantId: string
): Promise<ThreadCandidateBatch | null> {
  const { data } = await api.get(
    `/api/sessions/${sessionId}/participant-threads/${avatarId}/${participantId}/candidates`
  );
  return data.batch;
}

export async function confirmThreadCandidate(
  sessionId: string,
  avatarId: string,
  participantId: string,
  candidateId: string
): Promise<void> {
  await api.post(
    `/api/sessions/${sessionId}/participant-threads/${avatarId}/${participantId}/candidates/${candidateId}/confirm`
  );
}

export async function dismissThreadCandidate(
  sessionId: string,
  avatarId: string,
  participantId: string,
  candidateId: string
): Promise<void> {
  await api.post(
    `/api/sessions/${sessionId}/participant-threads/${avatarId}/${participantId}/candidates/${candidateId}/dismiss`
  );
}
